// Prüfung am Schreibtisch: Differenzliste je Lagerplatz und Artikel, Nachzählung anordnen, Gründe, Freigabe.
// Differenz = gezählt − Buchbestand zum Zählzeitpunkt. Der Wert (gleitender Durchschnitt) steht nur mit dem Recht
// „Einstandspreise sehen“ in der Ausgabe.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { APPROVE, COUNT_STATUSES, CREATE, READ, zBinRef, zItemRef, zLocationRef, zPerson } from './common'

export const DIFFERENCE_FILTERS = ['differences', 'large', 'recount', 'found', 'uncounted', 'all'] as const
export type DifferenceFilter = (typeof DIFFERENCE_FILTERS)[number]

export const zDifferenceRow = z.object({
  lineId: zId,
  bin: zBinRef,
  item: zItemRef,
  lotNo: z.string(),
  bestBefore: z.string().nullable(),
  /** nicht im Plan: beim Zählen gefunden */
  found: z.boolean(),
  /** Buchbestand beim Start der Inventur (Momentaufnahme); null bei Funden */
  frozenQty: z.int().nullable(),
  /** Buchbestand zum Zeitpunkt der maßgeblichen Zählung */
  bookQty: z.int().nullable(),
  countedQty: z.int().nullable(),
  /** gezählt − Buchbestand zum Zählzeitpunkt; null = noch nicht gezählt */
  differenceQty: z.int().nullable(),
  /** Wert der Differenz zum gleitenden Durchschnitt (±) – nur mit „Einstandspreise sehen“ */
  differenceValueCents: z.int().nullable().optional(),
  countedBy: zPerson.nullable(),
  countedAt: z.date().nullable(),
  /** alle Zählungen der Position – Erst- und Nachzählung im Vergleich */
  counts: z.array(z.object({ round: z.int(), countedQty: z.int(), bookQty: z.int().nullable(), by: zPerson.nullable(), at: z.date() })),
  recount: z.object({ state: z.enum(['requested', 'done']), requestedBy: zPerson.nullable(), assignedTo: zPerson.nullable(), note: z.string().nullable() }).nullable(),
  reasonCodeId: zId.nullable(),
  reasonName: z.string().nullable(),
  reason: z.string().nullable(),
  /** gebuchte Differenz (±) – nach dem Buchen */
  postedQty: z.int().nullable(),
})
export type DifferenceRow = z.output<typeof zDifferenceRow>

export const countDifferences = defineContract({
  name: 'stocktaking.count.differences',
  title: 'Inventurdifferenzen anzeigen',
  description: 'Differenzliste einer Inventur je Lagerplatz und Artikel – mit Erst- und Nachzählung und Grund.',
  llm: {
    description:
      'Liefert die Positionen einer Inventur mit Buchbestand zum Zählzeitpunkt (`bookQty`), Zählmenge (`countedQty`) ' +
      'und Differenz (`differenceQty` = gezählt − Buchbestand), alles in Tausendstel der Basiseinheit; ' +
      '`differenceValueCents` (Cent, gleitender Durchschnitt) nur mit „Einstandspreise sehen“. `filter`: ' +
      '`differences` (Standard, nur Abweichungen), `large` (Betrag ab `minValueCents` bzw. die größten), `recount` ' +
      '(offene Nachzählungen), `found` (Funde außerhalb des Plans), `uncounted`, `all`. `counts` nennt jede Zählung ' +
      'einer Position (Erst- und Nachzählung). Bei einer Blindzählung nur für Planung und Freigabe, bis gebucht ist. ' +
      'Ändert nichts; Nachzählen mit stocktaking.count.recount, Freigeben mit stocktaking.count.approve.',
    keywords: ['Inventurdifferenz', 'Abweichung', 'Fehlbestand', 'Mehrbestand', 'Schwund', 'Nachzählung', 'Differenzliste'],
  },
  input: z.strictObject({
    id: zId,
    filter: z.enum(DIFFERENCE_FILTERS).default('differences'),
    /** für `large`: Mindestbetrag der Differenz in Cent (braucht „Einstandspreise sehen“) */
    minValueCents: z.int().min(0).max(100_000_000).optional(),
    zone: z.string().trim().max(60).optional(),
  }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    name: z.string(),
    status: z.enum(COUNT_STATUSES),
    statusLabel: z.string(),
    location: zLocationRef,
    keyDate: z.string(),
    blind: z.boolean(),
    summary: z.object({
      positions: z.int(),
      counted: z.int(),
      differences: z.int(),
      shortLines: z.int(),
      overLines: z.int(),
      recountsOpen: z.int(),
      /** Summe der Beträge aller Differenzen in Cent – maßgeblich für die Freigabegrenze */
      valueCents: z.int().optional(),
      /** Saldo der Differenzen in Cent (Mehr − Minder) */
      netValueCents: z.int().optional(),
    }),
    rows: z.array(zDifferenceRow),
    /** wählbare Gründe je Position */
    reasons: z.array(z.object({ id: zId, name: z.string(), requiresNote: z.boolean() })),
    /** Personen, die eine Nachzählung übernehmen können */
    counters: z.array(zPerson),
    allowed: z.object({ recount: z.boolean(), reason: z.boolean(), approve: z.boolean() }),
    approveHint: z.string().nullable(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'record',
  fieldPermissions: {
    'rows.*.differenceValueCents': PERMISSION_COSTS_READ,
    'summary.valueCents': PERMISSION_COSTS_READ,
    'summary.netValueCents': PERMISSION_COSTS_READ,
  },
})

export const countRecount = defineContract({
  name: 'stocktaking.count.recount',
  title: 'Nachzählung anordnen',
  description: 'Ordnet für ausgewählte Positionen eine Nachzählung an – sie geht an eine andere Person als die Erstzählung.',
  llm: {
    description:
      'Ordnet für Positionen einer Inventur (`lineIds` aus stocktaking.count.differences) eine Nachzählung an. Sie ' +
      'muss von einer ANDEREN Person kommen als die bisherigen Zählungen der Position; `assignedUserId` nennt sie ' +
      '(sonst übernimmt, wer als Nächstes darf). Die Inventur steht danach wieder auf „Zählung läuft“. Gebucht wird ' +
      'nichts. Nur vor dem Buchen.',
    keywords: ['Nachzählung', 'nachzählen', 'Zweitzählung', 'Kontrollzählung anordnen'],
  },
  input: z.strictObject({
    id: zId,
    lineIds: z.array(zId).min(1, { error: 'Bitte wählen Sie mindestens eine Position.' }).max(200),
    assignedUserId: zId.nullish(),
    note: z.string().trim().max(300).nullish(),
  }),
  output: z.object({ id: zId, status: z.enum(COUNT_STATUSES), statusLabel: z.string(), requestedCount: z.int(), message: z.string() }),
  permission: CREATE,
  risk: 'write',
  scope: 'record',
  limits: { maxItems: 200, itemCount: (input) => input.lineIds.length },
})

export const countSetReason = defineContract({
  name: 'stocktaking.count.setReason',
  title: 'Grund einer Differenz setzen',
  description: 'Hält für Positionen einer Inventur den Grund der Abweichung fest (Bruch, Schwund, Fehlbuchung …).',
  llm: {
    description:
      'Setzt für Positionen einer Inventur den Grund der Abweichung: `reasonCodeId` (ein Grund der Art „Korrektur“ ' +
      'aus `reasons` von stocktaking.count.differences) und/oder eine Bemerkung `reason`; beides `null` nimmt den ' +
      'Grund zurück. Ändert weder Zählung noch Bestand. Nur vor dem Buchen.',
    keywords: ['Grund', 'Ursache', 'Schwund', 'Bruch', 'Fehlbuchung', 'Differenz begründen'],
  },
  input: z.strictObject({
    id: zId,
    lineIds: z.array(zId).min(1, { error: 'Bitte wählen Sie mindestens eine Position.' }).max(200),
    reasonCodeId: zId.nullish(),
    reason: z.string().trim().max(300).nullish(),
  }),
  output: z.object({ id: zId, updatedCount: z.int() }),
  permission: CREATE,
  risk: 'write',
  scope: 'record',
  limits: { maxItems: 200, itemCount: (input) => input.lineIds.length },
})

export const countApprove = defineContract({
  name: 'stocktaking.count.approve',
  title: 'Inventur freigeben und buchen',
  description: 'Gibt die Differenzen einer Inventur frei und bucht sie zum Stichtag über das Bestandsjournal.',
  llm: {
    description:
      'Gibt eine Inventur im Zustand „In Prüfung“ frei und bucht ihre Differenzen zum Stichtag (Bewegungsart ' +
      '„Inventurdifferenz“, bewertet zum gleitenden Durchschnitt). Voraussetzungen: alle Lagerplätze gezählt, keine ' +
      'Nachzählung offen, die Summe der Beträge aller Differenzen liegt innerhalb der Freigabegrenze der Rolle ' +
      '(`maxDifferenceCents`), und der Nutzer ist nicht die einzige Person, die gezählt hat (Vier-Augen-Prinzip – ' +
      'auch nicht bei einer einzelnen abweichenden Position). Danach ist die Inventur unveränderlich; ein Storno gibt ' +
      'es nicht – Fehler korrigiert eine neue Zählung oder eine Korrekturbuchung. Liegt der Stichtag in einer ' +
      'abgeschlossenen Periode, wird nichts gebucht.',
    keywords: ['Inventur freigeben', 'Inventur buchen', 'Differenzen buchen', 'Freigabe'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    status: z.enum(COUNT_STATUSES),
    statusLabel: z.string(),
    /** Zahl der gebuchten Differenzen */
    postedLines: z.int(),
    /** Saldo der gebuchten Differenzen in Cent (±) – nur mit „Einstandspreise sehen“ */
    netValueCents: z.int().optional(),
    message: z.string(),
  }),
  permission: APPROVE,
  risk: 'financial',
  scope: 'record',
  fieldPermissions: { netValueCents: PERMISSION_COSTS_READ },
})
